import React, { useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { Modal } from '../Modal';
import { EXTRACT_PORTFOLIO_DETAILS } from '../../graphql/EXTRACT_PORTFOLIO_DETAILS';
import { CREATE_PORTFOLIO } from '../../graphql/CREATE_PORTFOLIO';
import Upload, { EUploadTypes, getShortFilename } from '../Upload';

interface AddWorkModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

type Step = 'choice' | 'paste-link' | 'extracting' | 'review' | 'success' | 'manual';

const AddWorkModal: React.FC<AddWorkModalProps> = ({ isOpen, onClose, onSuccess }) => {
    const [step, setStep] = useState<Step>('choice');
    const [selectedChoice, setSelectedChoice] = useState<'link' | 'manual'>('link');
    const [link, setLink] = useState('');
    const [extractedData, setExtractedData] = useState<any>(null);
    const [extractionProgress, setExtractionProgress] = useState(0);

    React.useEffect(() => {
        if (step === 'extracting') {
            if (!extractedData) return;

            const timer = setInterval(() => {
                setExtractionProgress(p => {
                    if (p < 7) return p + 1;
                    return p;
                });
            }, 300);
            return () => clearInterval(timer);
        }
    }, [step, extractedData]);

    React.useEffect(() => {
        if (step === 'extracting' && extractionProgress === 7) {
            const timer = setTimeout(() => {
                setStep('review');
            }, 800);
            return () => clearTimeout(timer);
        }
    }, [step, extractionProgress]);

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [videoUrl, setVideoUrl] = useState('');
    const [role, setRole] = useState('Director');
    const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
    const [savedMedia, setSavedMedia] = useState<any[]>([]);

    // Validation states
    const [fileError, setFileError] = useState('');
    const [urlError, setUrlError] = useState('');

    const [extractDetails] = useLazyQuery(EXTRACT_PORTFOLIO_DETAILS, {
        onCompleted: (data) => {
            const details = data.extractPortfolioDetailsFromLink;
            setExtractedData(details);
            setTitle(details.title || '');
            const desc = (details.description || '').replace(/<br\s*\/?>/gi, '\n');
            setDescription(desc.substring(0, 500));
            setVideoUrl(details.videoUrl || '');
            setSelectedGenres(details.genres || []);
        },
        onError: () => {
            toast.error('Failed to extract details from link.');
            setStep('paste-link');
        }
    });

    const [createPortfolio, { loading: isCreating }] = useMutation(CREATE_PORTFOLIO, {
        refetchQueries: ['GET_USER_PORTFOLIOS'],
        awaitRefetchQueries: true,
        onCompleted: () => {
            setStep('success');
        },
        onError: (error) => {
            toast.error(error.message || 'Failed to save portfolio item');
        }
    });

    const handleSavePortfolio = async () => {
        if (!title.trim() || !description.trim()) {
            toast.error('Please fill in the required fields');
            return;
        }

        const data = {
            title: title.trim(),
            description: description.trim(),
            videoUrl: videoUrl.trim(),
            url: videoUrl.trim(),
            genres: selectedGenres,
            teamMembers: [role],
            image: (savedMedia.length > 0 ? savedMedia[0]?.fileUrl : null) || extractedData?.thumbnail || 'https://placehold.co/800x450/1A202C/8A94A6?text=No+Thumbnail+Provided'
        };

        try {
            await createPortfolio({ variables: { data } });
        } catch (e) {
            console.error(e);
        }
    };

    const handleExtract = async () => {
        if (!link) return;

        // Completely clear any leftover state from previous extractions or manual uploads
        setSavedMedia([]);
        setExtractedData(null);
        setTitle('');
        setDescription('');
        setVideoUrl('');
        setSelectedGenres([]);

        try {
            const urlObj = new URL(link);
            const validDomains = ['youtube.com', 'youtu.be', 'dailymotion.com', 'dai.ly'];
            const isValidDomain = validDomains.some(domain => urlObj.hostname.includes(domain));

            if (!isValidDomain) {
                setUrlError('Please enter a valid YouTube or Dailymotion link.');
                return;
            }

            setUrlError('');
        } catch {
            setUrlError('Please enter a valid URL (e.g. https://www.youtube.com/...)');
            return;
        }

        setStep('extracting');

        if (link.includes('dailymotion.com/video/') || link.includes('dai.ly/')) {
            let videoId = '';
            const dmMatch = link.match(/video\/([a-zA-Z0-9]+)/);
            const dailyMatch = link.match(/dai\.ly\/([a-zA-Z0-9]+)/);
            if (dmMatch) videoId = dmMatch[1];
            else if (dailyMatch) videoId = dailyMatch[1];

            if (videoId) {
                try {
                    const res = await fetch(`https://api.dailymotion.com/video/${videoId}?fields=title,description,thumbnail_720_url,tags`);
                    const data = await res.json();
                    if (data && data.title) {
                        // Simple client-side AI fallback to detect genres from Dailymotion text
                        const textContent = (data.title + ' ' + (data.description || '') + ' ' + (data.tags || []).join(' ')).toLowerCase();
                        const foundGenres: string[] = [];
                        if (textContent.includes('documentary') || textContent.includes('dokumentari')) foundGenres.push('Documentary');
                        if (textContent.includes('sci-fi') || textContent.includes('science fiction')) foundGenres.push('Sci-Fi');
                        if (textContent.includes('drama')) foundGenres.push('Drama');
                        if (textContent.includes('thriller')) foundGenres.push('Thriller');
                        if (textContent.includes('action')) foundGenres.push('Action');
                        if (textContent.includes('animation') || textContent.includes('anime') || textContent.includes('animated')) foundGenres.push('Animation');

                        const details = {
                            title: data.title,
                            description: (data.description || '').replace(/<br\s*\/?>/gi, '\n'),
                            thumbnail: data.thumbnail_720_url || '',
                            genres: foundGenres,
                            tags: data.tags || [],
                            teamMembers: [],
                            videoUrl: link
                        };
                        setExtractedData(details);
                        setTitle(details.title);
                        const desc = details.description || '';
                        setDescription(desc.substring(0, 500));
                        setVideoUrl(details.videoUrl);
                        setSelectedGenres(details.genres || []);
                        return;
                    }
                } catch (e) {
                    console.error('Dailymotion API fetch failed:', e);
                }
            }
        }

        extractDetails({ variables: { url: link } });
    };

    const handleManual = () => {
        setStep('manual');
    };

    const resetState = () => {
        setSelectedChoice('link');
        setLink('');
        setExtractedData(null);
        setExtractionProgress(0);
        setTitle('');
        setDescription('');
        setVideoUrl('');
        setSelectedGenres([]);
        setRole('Director');
        setSavedMedia([]);
        setFileError('');
        setUrlError('');
    };

    const handleClose = () => {
        setStep('choice');
        resetState();
        onClose();
    };

    const handleBackToChoice = () => {
        setStep('choice');
        resetState();
    };

    const handleContinueChoice = () => {
        if (selectedChoice === 'link') {
            setStep('paste-link');
        } else {
            handleManual();
        }
    };

    if (!isOpen) return null;

    return (
        <Modal isOpen={isOpen} onClose={handleClose}>
            {step === 'choice' && (
                <div className="portfolio-form add-work-modal">
                    <div className="portfolio-form__header m-b-2" style={{ justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
                        <button
                            className="is-clickable"
                            onClick={handleClose}
                            type="button"
                            style={{ border: 'none', background: '#F1F5F9', borderRadius: '50%', padding: 0, margin: 0, width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B', cursor: 'pointer' }}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                        </button>
                    </div>

                    <div className="has-text-centered" style={{ marginBottom: '2rem' }}>
                        <h3 className="title is-4" style={{ color: '#223141', marginBottom: '0.25rem' }}>How would you like to add your work?</h3>
                        <p className="desc-text">Choose the option that works best for you.</p>
                    </div>

                    <div
                        className={`choice-box is-clickable ${selectedChoice === 'link' ? 'is-recommended' : ''}`}
                        style={selectedChoice === 'link' ? { borderColor: 'rgb(87, 117, 255)', boxShadow: '0 4px 12px rgba(87, 117, 255, 0.1)' } : {}}
                        onClick={() => setSelectedChoice('link')}
                    >
                        <div className="is-flex is-align-items-center">
                            <span className="choice-icon">🚀</span>
                            <div>
                                <div>
                                    <h4 className="choice-title is-hidden-mobile" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                                        <span>Import from a link</span>
                                        <span className="tag is-recommended-tag" style={{ margin: 0 }}>RECOMMENDED</span>
                                    </h4>
                                    <div className="is-hidden-tablet" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', marginBottom: '0.25rem' }}>
                                        <h4 className="choice-title" style={{ marginBottom: '0.25rem' }}>Import from a link</h4>
                                        <span className="tag is-recommended-tag" style={{ margin: 0 }}>RECOMMENDED</span>
                                    </div>
                                </div>
                                <p className="desc-text" style={{ fontSize: '0.8rem' }}>Paste a YouTube or Dailymotion link. Matchi automatically extracts your title, thumbnail, description and tags.</p>
                            </div>
                            <span className="icon has-text-info ml-auto">
                                <i className={`fas ${selectedChoice === 'link' ? 'fa-check-circle' : 'fa-circle'}`} style={{ color: selectedChoice === 'link' ? 'rgb(87, 117, 255)' : '#DEE1EE' }}></i>
                            </span>
                        </div>
                    </div>

                    <div
                        className={`choice-box is-clickable ${selectedChoice === 'manual' ? 'is-recommended' : ''}`}
                        style={selectedChoice === 'manual' ? { borderColor: 'rgb(87, 117, 255)', boxShadow: '0 4px 12px rgba(87, 117, 255, 0.1)' } : {}}
                        onClick={() => setSelectedChoice('manual')}
                    >
                        <div className="is-flex is-align-items-center">
                            <span className="choice-icon">✍️</span>
                            <div>
                                <h4 className="choice-title">Add manually</h4>
                                <p className="desc-text" style={{ fontSize: '0.8rem' }}>Create your portfolio from scratch.</p>
                            </div>
                            <span className="icon has-text-grey ml-auto">
                                <i className={`fas ${selectedChoice === 'manual' ? 'fa-check-circle' : 'fa-circle'}`} style={{ color: selectedChoice === 'manual' ? 'rgb(87, 117, 255)' : '#DEE1EE' }}></i>
                            </span>
                        </div>
                    </div>

                    <div className="has-text-centered" style={{ marginTop: '1.5rem' }}>
                        <button
                            className="button is-fullwidth is-rounded"
                            style={{
                                height: '48px',
                                fontWeight: 600,
                                marginBottom: '0.75rem',
                                backgroundColor: selectedChoice ? '#5775FF' : '#F1F5F9',
                                color: selectedChoice ? '#FFFFFF' : '#8A94A6',
                                border: 'none',
                                transition: 'all 0.2s ease'
                            }}
                            onClick={handleContinueChoice}
                            disabled={!selectedChoice}
                        >
                            Continue
                        </button>
                        <p className="desc-text" style={{ fontSize: '0.75rem' }}>You can review everything before publishing.</p>
                    </div>
                </div>
            )}

            {step === 'manual' && (
                <div className="portfolio-form add-work-modal" style={{ padding: '0', backgroundColor: '#FFFFFF', borderRadius: '16px' }}>
                    <div className="is-flex is-align-items-center" style={{ padding: '1.5rem', borderBottom: '1px solid #F1F5F9' }}>
                        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start' }}>
                            <button
                                onClick={() => setStep('choice')}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }}
                            >
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="#0F172A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                        </div>
                        <div style={{ flex: 1, textAlign: 'center', fontSize: '1rem', fontWeight: 700, color: '#0F172A', whiteSpace: 'nowrap' }}>Add manually</div>
                        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                            <button
                                className="is-clickable"
                                onClick={handleClose}
                                type="button"
                                style={{ border: 'none', background: '#F1F5F9', borderRadius: '50%', padding: 0, margin: 0, width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B', cursor: 'pointer' }}
                            >
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                            </button>
                        </div>
                    </div>

                    <div className="form-content" style={{ padding: '1.5rem' }}>
                        {/* Thumbnail Upload */}
                        <div className="field" style={{ marginBottom: '1.5rem' }}>
                            <label className="label" style={{ color: '#8A94A6', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>Thumbnail</label>
                            {savedMedia.length > 0 ? (
                                <div style={{ border: '1px solid #E2E8F0', borderRadius: '8px', padding: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', minWidth: 0, flex: 1 }}>
                                        <div style={{ width: '48px', height: '48px', borderRadius: '4px', overflow: 'hidden', backgroundColor: '#F1F5F9', flexShrink: 0 }}>
                                            <img src={savedMedia[0].fileUrl} alt="Thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        </div>
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                            <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#223141' }}>Image uploaded</div>
                                            <div style={{ fontSize: '0.8rem', color: '#8A94A6', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getShortFilename(savedMedia[0].fileUrl)}</div>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setSavedMedia([])}
                                        style={{ background: 'none', border: 'none', color: '#EF4444', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
                                    >
                                        Remove
                                    </button>
                                </div>
                            ) : (
                                <Upload
                                    uploadContainer={() => (
                                        <div style={{ width: '100%', border: '1px dashed #CBD5E1', borderRadius: '8px', backgroundColor: '#FFFFFF', padding: '3rem 0', cursor: 'pointer', transition: 'all 0.2s' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <span style={{ color: '#0F172A', fontSize: '0.95rem', fontWeight: 600 }}>Click or drag to upload image</span>
                                                <span style={{ color: '#94A3B8', fontSize: '0.85rem', marginTop: '0.25rem' }}>Maximum file size: 5MB</span>
                                            </div>
                                        </div>
                                    )}
                                    uploadType={EUploadTypes.project}
                                    formType="new"
                                    medias={savedMedia}
                                    setSavedMedia={(val: any) => {
                                        if (typeof val === 'function') {
                                            const newMedia = val([]);
                                            if (newMedia.length > 0) setSavedMedia([newMedia[newMedia.length - 1]]);
                                        } else {
                                            setSavedMedia(Array.isArray(val) ? val : [val]);
                                        }
                                    }}
                                />
                            )}
                            {fileError && <p style={{ color: '#EF4444', fontSize: '0.8rem', marginTop: '0.5rem' }}>{fileError}</p>}
                        </div>

                        {/* Title */}
                        <div className="field" style={{ marginBottom: '1.5rem' }}>
                            <label className="label" style={{ color: '#8A94A6', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                                Title <span style={{ color: '#EF4444' }}>*</span>
                            </label>
                            <div className="control">
                                <input className="input" type="text" placeholder="Portfolio item title" value={title} onChange={(e) => setTitle(e.target.value)} style={{ borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#FFFFFF', boxShadow: 'none', height: '48px', color: '#223141', fontWeight: 400 }} />
                            </div>
                        </div>

                        {/* Description */}
                        <div className="field" style={{ marginBottom: '1.5rem' }}>
                            <label className="label" style={{ color: '#8A94A6', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                                Description <span style={{ color: '#EF4444' }}>*</span>
                            </label>
                            <div className="control" style={{ position: 'relative' }}>
                                <textarea className="textarea" placeholder="Tell us about this project..." value={description} onChange={(e) => setDescription(e.target.value)} rows={4} maxLength={500} style={{ borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#FFFFFF', boxShadow: 'none', color: '#223141', fontWeight: 400, resize: 'none', paddingBottom: '24px' }}></textarea>
                                <div style={{ position: 'absolute', bottom: '8px', right: '12px', fontSize: '0.75rem', color: '#94A3B8' }}>
                                    {description.length}/500
                                </div>
                            </div>
                        </div>

                        {/* Role */}
                        <div className="field" style={{ marginBottom: '1.5rem' }}>
                            <label className="label" style={{ color: '#8A94A6', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>Your role</label>
                            <div className="control">
                                <div className="select is-fullwidth">
                                    <select value={role} onChange={(e) => setRole(e.target.value)} style={{ borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#FFFFFF', boxShadow: 'none', height: '48px', color: '#223141', fontWeight: 400 }}>
                                        <option value="Director">Director</option>
                                        <option value="Producer">Producer</option>
                                        <option value="Editor">Editor</option>
                                        <option value="Cinematographer">Cinematographer</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Video URL */}
                        <div className="field" style={{ marginBottom: '1.5rem' }}>
                            <label className="label" style={{ color: '#8A94A6', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>Video URL</label>
                            <div className="control">
                                <input className="input" type="text" placeholder="https://example.com" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} style={{ borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#FFFFFF', boxShadow: 'none', height: '48px', color: '#223141', fontWeight: 400 }} />
                            </div>
                        </div>

                        {/* Genres */}
                        <div className="field" style={{ marginBottom: '2rem' }}>
                            <label className="label" style={{ color: '#8A94A6', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>Genres</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                {Array.from(new Set(['Sci-Fi', 'Drama', 'Thriller', 'Action', 'Animation', 'Documentary', ...selectedGenres])).map(genre => {
                                    const isSelected = selectedGenres.includes(genre);
                                    return (
                                        <button
                                            key={genre}
                                            type="button"
                                            onClick={() => {
                                                if (isSelected) {
                                                    setSelectedGenres(selectedGenres.filter(g => g !== genre));
                                                } else {
                                                    setSelectedGenres([...selectedGenres, genre]);
                                                }
                                            }}
                                            style={{
                                                padding: '6px 14px',
                                                borderRadius: '24px',
                                                border: `1px solid ${isSelected ? '#6366F1' : '#E2E8F0'}`,
                                                backgroundColor: isSelected ? '#EEF2FF' : '#FFFFFF',
                                                color: isSelected ? '#6366F1' : '#475569',
                                                fontSize: '0.8rem',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            {genre}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Submit Button */}
                        <button
                            className={`button is-fullwidth ${isCreating ? 'is-loading' : ''}`}
                            style={{ backgroundColor: '#5775FF', color: 'white', height: '48px', borderRadius: '24px', fontWeight: 600, fontSize: '1rem', border: 'none' }}
                            onClick={handleSavePortfolio}
                            disabled={isCreating}
                        >
                            {isCreating ? 'Saving...' : 'Save Portfolio'}
                        </button>
                    </div>
                </div>
            )}

            {step === 'paste-link' && (
                <div className="portfolio-form add-work-modal">
                    <div className="is-flex is-justify-content-space-between is-align-items-center" style={{ marginBottom: '2rem' }}>
                        <button className="is-clickable" onClick={handleBackToChoice} style={{ border: 'none', background: 'transparent', padding: 0, width: '24px', display: 'flex', alignItems: 'center', fontSize: '1.25rem', color: '#223141', cursor: 'pointer' }}>
                            ←
                        </button>
                        <h4 className="m-0 has-text-weight-bold" style={{ color: '#223141', flex: 1, textAlign: 'center', fontSize: '1.1rem' }}>Import from link</h4>
                        <button className="is-clickable" onClick={handleClose} type="button" style={{ border: 'none', background: '#F1F5F9', borderRadius: '50%', padding: 0, margin: 0, width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B', cursor: 'pointer' }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                        </button>
                    </div>

                    <div style={{ marginBottom: '1.5rem' }}>
                        <h3 className="has-text-weight-bold" style={{ color: '#223141', fontSize: '1.5rem', marginBottom: '0.5rem' }}>Paste a link to your work</h3>
                        <p style={{ color: '#8A94A6', fontSize: '0.9rem' }}>We support YouTube and Dailymotion.</p>
                    </div>
                    <div className="is-flex is-flex-wrap-wrap" style={{ gap: '1rem', marginBottom: '1.5rem' }}>
                        {[
                            {
                                name: 'YouTube',
                                color: '#FF0000',
                                iconSvg: <svg viewBox="0 0 24 24" width="16" height="16" fill="#FF0000"><path d="M21.582,6.186c-0.23-0.86-0.908-1.538-1.768-1.768C18.254,4,12,4,12,4S5.746,4,4.186,4.418c-0.86,0.23-1.538,0.908-1.768,1.768C2,7.746,2,12,2,12s0,4.254,0.418,5.814c0.23,0.86,0.908,1.538,1.768,1.768C5.746,20,12,20,12,20s6.254,0,7.814-0.418c0.86-0.23,1.538-0.908,1.768-1.768C22,16.254,22,12,22,12S22,7.746,21.582,6.186z M9.996,15.005l0-6.01l5.518,3.005L9.996,15.005z"/></svg>
                            },
                            {
                                name: 'Dailymotion',
                                color: '#0062D2',
                                iconSvg: <svg viewBox="0 0 24 24" width="16" height="16"><circle cx="12" cy="12" r="12" fill="#0062D2"/><text x="12" y="17" fill="#fff" fontFamily="Arial, sans-serif" fontSize="16" fontWeight="bold" textAnchor="middle">d</text></svg>
                            }
                        ].map((platform) => (
                            <button
                                key={platform.name}
                                className="is-flex is-align-items-center"
                                style={{
                                    justifyContent: 'center',
                                    color: '#223141',
                                    backgroundColor: '#FFFFFF',
                                    border: '1px solid #E2E8F0',
                                    borderRadius: '24px',
                                    padding: '10px 16px',
                                    fontWeight: 600,
                                    fontSize: '0.9rem',
                                    cursor: 'default',
                                    boxShadow: 'none'
                                }}
                            >
                                <span style={{ width: '16px', height: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginRight: '8px' }}>
                                    {platform.iconSvg ? platform.iconSvg : <span style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: platform.color }}></span>}
                                </span>
                                {platform.name}
                            </button>
                        ))}
                    </div>

                    <div className="field" style={{ marginBottom: '0.25rem' }}>
                        <label className="label" style={{ fontSize: '0.9rem', color: '#223141', fontWeight: 600, marginBottom: '0.5rem' }}>Paste URL</label>
                        <div className="control">
                            <input
                                className="input is-medium"
                                type="url"
                                placeholder="https://www.youtube.com/watch?v=..."
                                value={link}
                                onChange={(e) => {
                                    setLink(e.target.value);
                                    setUrlError('');
                                }}
                                style={{
                                    borderRadius: '8px',
                                    border: urlError ? '1px solid #EF4444' : '1px solid #DEE1EE',
                                    backgroundColor: '#FFFFFF',
                                    color: '#223141',
                                    fontSize: '1rem',
                                    paddingLeft: '1rem',
                                    boxShadow: 'none',
                                    height: '52px'
                                }}
                            />
                        </div>
                        {urlError && <p style={{ color: '#EF4444', fontSize: '0.85rem', marginTop: '0.5rem' }}>{urlError}</p>}
                    </div>
                    <p style={{ color: '#8A94A6', fontSize: '0.85rem', marginTop: '0' }}>We'll extract the title, description, thumbnail and more.</p>

                    <button
                        className="button is-fullwidth is-rounded"
                        onClick={handleExtract}
                        disabled={!link}
                        style={{
                            height: '48px',
                            fontWeight: 600,
                            marginTop: '2rem',
                            backgroundColor: link ? '#5775FF' : '#F1F5F9',
                            color: link ? '#FFFFFF' : '#8A94A6',
                            border: 'none',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        Continue
                    </button>
                </div>
            )}

            {step === 'extracting' && (
                <div className="portfolio-form add-work-modal" style={{ padding: '2rem 1.5rem' }}>
                    <div className="has-text-centered" style={{ marginBottom: '1.5rem' }}>
                        <h3 className="has-text-weight-bold" style={{ color: '#223141', fontSize: '1.25rem', marginBottom: '0.5rem' }}>Extracting details...</h3>
                        <p style={{ color: '#8A94A6', fontSize: '0.9rem', lineHeight: '1.4', maxWidth: '260px', margin: '0 auto' }}>
                            Our AI is fetching information from the link you provided.
                        </p>
                    </div>

                    <div style={{ position: 'relative', width: '100%', height: '160px', borderRadius: '12px', backgroundColor: '#1A202C', overflow: 'hidden', marginBottom: '1.5rem' }}>
                        {extractedData?.thumbnail ? (
                            <img src={extractedData.thumbnail} alt="Extracting preview" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.7 }} />
                        ) : (
                            <div style={{ width: '100%', height: '100%', backgroundColor: '#2D3748', animation: 'pulse 1.5s infinite' }}></div>
                        )}
                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '48px', height: '48px', backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <div style={{ width: 0, height: 0, borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderLeft: '12px solid #223141', marginLeft: '4px' }}></div>
                        </div>

                    </div>

                    <div style={{ border: '1px solid #E2E8F0', borderRadius: '12px', padding: '0 1rem', marginBottom: '1.5rem', backgroundColor: '#FFFFFF' }}>
                        {[
                            { label: 'Title', value: extractedData?.title || '' },
                            { label: 'Description', value: extractedData?.description ? (extractedData.description.length > 25 ? extractedData.description.substring(0, 25) + '...' : extractedData.description) : '' },
                            { label: 'Thumbnail', value: '', isImage: true },
                            { label: 'Genres', value: extractedData?.genres?.length ? extractedData.genres.join(', ') : '' },
                            { label: 'Tags', value: extractedData?.tags?.length ? extractedData.tags.join(', ') : '' },
                            { label: 'Team members', value: extractedData?.teamMembers?.length ? `${extractedData.teamMembers.length} found` : (extractedData ? '0 found' : '') },
                            { label: 'More details', value: '', isLast: true }
                        ].map((item, idx) => {
                            const isDone = extractionProgress > idx;
                            const isActive = extractionProgress === idx;
                            return (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: item.isLast ? 'none' : '1px solid #F1F5F9' }}>
                                    <div style={{ opacity: isDone || isActive ? 1 : 0.4, transition: 'opacity 0.3s' }}>
                                        <div style={{ color: '#223141', fontSize: '0.85rem', fontWeight: 600, marginBottom: ((isDone && item.value) || item.isImage) ? '0.25rem' : '0' }}>{item.label}</div>
                                        {(isDone && item.value) && <div style={{ color: '#8A94A6', fontSize: '0.75rem' }}>{item.value}</div>}
                                        {item.isImage && (
                                            <div style={{ width: '48px', height: '28px', borderRadius: '4px', overflow: 'hidden', backgroundColor: '#E2E8F0' }}>
                                                {(isDone && extractedData?.thumbnail) && (
                                                    <img src={extractedData.thumbnail} alt="Thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        {isDone ? (
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}>
                                                <circle cx="12" cy="12" r="10"></circle>
                                                <polyline points="16 8 10 16 6 12"></polyline>
                                            </svg>
                                        ) : (
                                            <div style={{
                                                width: '20px',
                                                height: '20px',
                                                borderRadius: '50%',
                                                border: `2px solid ${isActive ? '#E0E7FF' : '#F1F5F9'}`,
                                                borderTopColor: isActive ? '#5775FF' : '#94A3B8',
                                                animation: 'spin 1s linear infinite'
                                            }}></div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <style>{`
                        @keyframes popIn {
                            0% { transform: scale(0.5); opacity: 0; }
                            100% { transform: scale(1); opacity: 1; }
                        }
                        @keyframes pulse {
                            0% { opacity: 0.6; }
                            50% { opacity: 0.8; }
                            100% { opacity: 0.6; }
                        }
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `}</style>

                    <div style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F8FAFC', borderRadius: '8px', padding: '1rem', color: '#223141' }}>
                        <span style={{ fontSize: '1.25rem', marginRight: '0.75rem', color: '#6366F1' }}>✨</span>
                        <div style={{ fontSize: '0.85rem', lineHeight: '1.4' }}>
                            <span style={{ fontWeight: 700 }}>Almost done!</span> You can review everything in the next step.
                        </div>
                    </div>
                </div>
            )}

            {step === 'review' && extractedData && (
                <div className="portfolio-form add-work-modal" style={{ padding: '0', backgroundColor: '#FFFFFF', borderRadius: '16px' }}>
                    <div className="is-flex is-align-items-center" style={{ padding: '1.5rem', borderBottom: '1px solid #F1F5F9' }}>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                            <button className="is-clickable" onClick={() => setStep('paste-link')} style={{ border: 'none', background: 'transparent', padding: 0, width: '24px', display: 'flex', alignItems: 'center', fontSize: '1.25rem', color: '#223141', cursor: 'pointer' }}>
                                ←
                            </button>
                        </div>
                        <h4 className="m-0 has-text-weight-bold" style={{ color: '#223141', textAlign: 'center', fontSize: '1.1rem' }}>Review & edit</h4>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>

                        </div>
                    </div>

                    <div className="form-content" style={{ padding: '1.5rem' }}>
                        <div className="field" style={{ marginBottom: '1.5rem' }}>
                            <label className="label" style={{ color: '#8A94A6', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>Thumbnail</label>
                            <div style={{ position: 'relative', width: '100%', height: '180px', borderRadius: '12px', overflow: 'hidden', backgroundColor: '#1A202C' }}>
                                <img src={savedMedia.length > 0 ? savedMedia[0].fileUrl : (extractedData.thumbnail || 'https://placehold.co/800x450/1A202C/8A94A6?text=No+Thumbnail+Provided')} alt="Thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.8 }} />
                                {!savedMedia.length && (
                                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '48px', height: '48px', backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                                        <div style={{ width: 0, height: 0, borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderLeft: '12px solid #223141', marginLeft: '4px' }}></div>
                                    </div>
                                )}
                                <Upload
                                    uploadContainer={() => (
                                        <button style={{ position: 'absolute', bottom: '12px', right: '12px', backgroundColor: '#FFFFFF', color: '#223141', border: 'none', borderRadius: '24px', padding: '6px 16px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                                            Change
                                        </button>
                                    )}
                                    uploadType={EUploadTypes.project}
                                    formType="new"
                                    medias={savedMedia}
                                    setSavedMedia={(val: any) => {
                                        if (typeof val === 'function') {
                                            const newMedia = val([]);
                                            if (newMedia.length > 0) setSavedMedia([newMedia[newMedia.length - 1]]);
                                        } else {
                                            setSavedMedia(Array.isArray(val) ? val : [val]);
                                        }
                                    }}
                                />
                            </div>
                        </div>

                        <div className="field" style={{ marginBottom: '1.5rem' }}>
                            <label className="label" style={{ color: '#8A94A6', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>Title <span style={{ color: '#EF4444' }}>*</span></label>
                            <div className="control">
                                <input className="input" type="text" value={title} onChange={(e) => setTitle(e.target.value)} style={{ borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#FFFFFF', boxShadow: 'none', color: '#223141', fontWeight: 400 }} />
                            </div>
                        </div>

                        <div className="field" style={{ marginBottom: '1.5rem' }}>
                            <label className="label" style={{ color: '#8A94A6', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>Description <span style={{ color: '#EF4444' }}>*</span></label>
                            <div className="control" style={{ position: 'relative' }}>
                                <textarea className="textarea" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} maxLength={500} style={{ borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#FFFFFF', boxShadow: 'none', color: '#223141', fontWeight: 400, resize: 'none', paddingBottom: '24px' }}></textarea>
                                <span style={{ position: 'absolute', bottom: '8px', right: '12px', fontSize: '0.7rem', color: '#8A94A6' }}>{description.length}/500</span>
                            </div>
                        </div>

                        <div className="field" style={{ marginBottom: '1.5rem' }}>
                            <label className="label" style={{ color: '#8A94A6', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>Your role</label>
                            <div className="control">
                                <div className="select is-fullwidth" style={{ borderRadius: '8px' }}>
                                    <select value={role} onChange={(e) => setRole(e.target.value)} style={{ borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#FFFFFF', height: '48px', color: '#223141', fontWeight: 400, boxShadow: 'none' }}>
                                        <option value="Director">Director</option>
                                        <option value="Producer">Producer</option>
                                        <option value="Editor">Editor</option>
                                        <option value="Actor">Actor</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="field" style={{ marginBottom: '1.5rem' }}>
                            <label className="label" style={{ color: '#8A94A6', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>Video URL</label>
                            <div className="control">
                                <input className="input" type="url" value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)} style={{ borderRadius: '8px', border: '1px solid #E2E8F0', backgroundColor: '#FFFFFF', boxShadow: 'none', color: '#223141', fontWeight: 400 }} />
                            </div>
                        </div>

                        <div className="field" style={{ marginBottom: '2rem' }}>
                            <label className="label" style={{ color: '#8A94A6', fontSize: '0.85rem', fontWeight: 600, marginBottom: '0.5rem' }}>Genres</label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                                {Array.from(new Set(['Sci-Fi', 'Drama', 'Thriller', 'Action', 'Animation', 'Documentary', ...selectedGenres])).map(genre => {
                                    const isSelected = selectedGenres.includes(genre);
                                    return (
                                        <button
                                            key={genre}
                                            type="button"
                                            onClick={() => {
                                                if (isSelected) {
                                                    setSelectedGenres(prev => prev.filter(g => g !== genre));
                                                } else {
                                                    setSelectedGenres(prev => [...prev, genre]);
                                                }
                                            }}
                                            style={{
                                                padding: '6px 14px',
                                                borderRadius: '24px',
                                                border: isSelected ? '1px solid #5775FF' : '1px solid #E2E8F0',
                                                backgroundColor: isSelected ? '#EEF2FF' : '#FFFFFF',
                                                color: isSelected ? '#5775FF' : '#475569',
                                                fontSize: '0.8rem',
                                                fontWeight: 600,
                                                cursor: 'pointer'
                                            }}
                                        >
                                            {genre}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        <button
                            className="button is-fullwidth is-rounded"
                            onClick={handleSavePortfolio}
                            disabled={isCreating}
                            style={{ height: '48px', fontWeight: 600, backgroundColor: '#5775FF', color: '#FFFFFF', border: 'none' }}
                        >
                            {isCreating ? 'Saving...' : 'Save Portfolio'}
                        </button>
                    </div>
                </div>
            )}

            {step === 'success' && (
                <div className="portfolio-form add-work-modal has-text-centered" style={{ padding: '1.5rem', backgroundColor: '#FFFFFF', borderRadius: '16px' }}>
                    <div className="is-flex is-justify-content-space-between is-align-items-center" style={{ marginBottom: '1.5rem' }}>
                        <div style={{ width: '24px' }}></div>
                        <h4 className="m-0 has-text-weight-bold" style={{ color: '#223141', flex: 1, textAlign: 'center', fontSize: '1.1rem' }}>Add Work</h4>
                        <button className="is-clickable" onClick={handleClose} type="button" style={{ border: 'none', background: '#F1F5F9', borderRadius: '50%', padding: 0, margin: 0, width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B', cursor: 'pointer' }}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                        </button>
                    </div>

                    <div style={{ position: 'relative', display: 'inline-flex', justifyContent: 'center', marginBottom: '1.5rem', marginTop: '1rem' }}>
                        <div style={{ width: '64px', height: '64px', backgroundColor: '#5775FF', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 24px rgba(87, 117, 255, 0.3)' }}>
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="20 6 9 17 4 12"></polyline>
                            </svg>
                        </div>
                        <div style={{ position: 'absolute', top: '-10px', left: '-20px', fontSize: '1.1rem', color: '#F59E0B' }}>✦</div>
                        <div style={{ position: 'absolute', top: '10px', right: '-25px', fontSize: '0.8rem', color: '#EC4899' }}>✦</div>
                        <div style={{ position: 'absolute', bottom: '-5px', left: '-15px', fontSize: '0.9rem', color: '#3B82F6' }}>✦</div>
                        <div style={{ position: 'absolute', bottom: '20px', right: '-30px', fontSize: '1rem', color: '#8B5CF6' }}>✦</div>
                    </div>

                    <h3 className="has-text-weight-bold" style={{ color: '#223141', fontSize: '1.5rem', marginBottom: '0.5rem' }}>Added to your profile!</h3>
                    <p style={{ color: '#8A94A6', fontSize: '1rem', marginBottom: '2rem', maxWidth: '320px', margin: '0 auto 2rem' }}>Your work is now visible on your portfolio.</p>

                    <div style={{ border: '1px solid #E2E8F0', borderRadius: '16px', padding: '1rem', marginBottom: '2rem', textAlign: 'left', backgroundColor: '#FFFFFF', boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
                        <div style={{ position: 'relative', width: '100%', height: '180px', borderRadius: '8px', overflow: 'hidden', marginBottom: '1rem', backgroundColor: '#1A202C' }}>
                            <img src={(savedMedia.length > 0 ? savedMedia[0]?.fileUrl : null) || extractedData?.thumbnail || 'https://placehold.co/800x450/1A202C/8A94A6?text=No+Thumbnail+Provided'} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover', opacity: 0.8 }} />
                            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '48px', height: '48px', backgroundColor: 'rgba(255,255,255,0.95)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <div style={{ width: 0, height: 0, borderTop: '8px solid transparent', borderBottom: '8px solid transparent', borderLeft: '12px solid #223141', marginLeft: '4px' }}></div>
                            </div>

                        </div>
                        <h4 style={{ color: '#223141', fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.25rem' }}>{title || 'The Neon City'}</h4>
                        <p style={{ color: '#8A94A6', fontSize: '0.85rem', marginBottom: '1rem' }}>
                            Short Film • {selectedGenres.length > 0 ? selectedGenres.join(', ') : 'Sci-Fi, Drama'}
                        </p>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{ display: 'flex', marginRight: '0.5rem' }}>
                                <img src="https://i.pravatar.cc/100?img=1" alt="Avatar" style={{ width: '28px', height: '28px', borderRadius: '50%', border: '2px solid #FFFFFF', marginLeft: '0', backgroundColor: '#E2E8F0' }} />
                                <img src="https://i.pravatar.cc/100?img=2" alt="Avatar" style={{ width: '28px', height: '28px', borderRadius: '50%', border: '2px solid #FFFFFF', marginLeft: '-8px', backgroundColor: '#E2E8F0' }} />
                                <img src="https://i.pravatar.cc/100?img=3" alt="Avatar" style={{ width: '28px', height: '28px', borderRadius: '50%', border: '2px solid #FFFFFF', marginLeft: '-8px', backgroundColor: '#E2E8F0' }} />
                            </div>
                            <span style={{ color: '#8A94A6', fontSize: '0.8rem', fontWeight: 600 }}>+2</span>
                        </div>
                    </div>

                    <p style={{ color: '#223141', fontSize: '1rem', fontWeight: 700, marginBottom: '1rem' }}>What's next?</p>
                    <button
                        className="button is-fullwidth is-rounded"
                        onClick={handleBackToChoice}
                        style={{ height: '48px', fontWeight: 600, backgroundColor: '#5775FF', color: '#FFFFFF', border: 'none', marginBottom: '0.75rem' }}
                    >
                        Add another work
                    </button>
                    <button
                        className="button is-fullwidth is-rounded"
                        style={{ height: '48px', fontWeight: 600, backgroundColor: '#FFFFFF', color: '#5775FF', border: '1px solid #D6E0FF' }}
                        onClick={() => { onSuccess(); handleClose(); }}
                    >
                        View my profile
                    </button>
                </div>
            )}

        </Modal>
    );
};

export default AddWorkModal;
